import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { CommentAdminDto, CommentAdminRequest, ContentType } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { formatDate } from "../lib/format";
import { AdminTable, type Column } from "./AdminTable";
import { AdminForm, type FieldDef, type FormValues } from "./AdminForm";
import type { AdminOutletContext } from "./AdminLayout";

const CONTENT_TYPES: ContentType[] = ["BLOG_POST", "PHOTO", "VIDEO", "LINK", "DOCUMENT", "ARTICLE"];

const FIELDS: FieldDef[] = [
  { name: "authorName", label: "Author", type: "text" },
  { name: "body", label: "Comment", type: "textarea" },
  { name: "deleted", label: "Removed (soft-deleted)", type: "checkbox" },
];

function toForm(c: CommentAdminDto): FormValues {
  return { authorName: c.authorName, body: c.body, deleted: c.deleted };
}

function toRequest(v: FormValues): CommentAdminRequest {
  return {
    authorName: String(v.authorName ?? "").trim() || undefined,
    body: String(v.body ?? "").trim() || undefined,
    deleted: !!v.deleted,
  };
}

function truncate(text: string, n = 80): string {
  return text.length > n ? text.slice(0, n) + "…" : text;
}

export function AdminComments() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data, loading, error } = useAsync(() => adminApi.listComments(), [refreshToken, reload]);
  const rows = data ?? [];
  const [editing, setEditing] = useState<CommentAdminDto | null>(null);
  const refetch = () => setReload((n) => n + 1);

  const columns: Column<CommentAdminDto>[] = [
    {
      key: "id",
      header: "#",
      render: (c) => <span className="mono">{c.id}</span>,
      sortValue: (c) => c.id,
    },
    {
      key: "on",
      header: "On",
      render: (c) => (
        <Link className="mono" to={`/documents/${c.contentId}`}>
          {c.contentId}
        </Link>
      ),
      sortValue: (c) => c.contentId,
    },
    {
      key: "author",
      header: "Author",
      render: (c) => c.authorName,
      sortValue: (c) => c.authorName,
    },
    {
      key: "body",
      header: "Comment",
      render: (c) => <span className="cell-muted">{truncate(c.body)}</span>,
    },
    {
      key: "score",
      header: "Score",
      render: (c) => <span className="mono">{c.score}</span>,
      sortValue: (c) => c.score,
    },
    {
      key: "status",
      header: "Status",
      render: (c) =>
        c.deleted ? (
          <span className="pill pill--off">removed</span>
        ) : (
          <span className="pill pill--on">visible</span>
        ),
      sortValue: (c) => (c.deleted ? 1 : 0),
    },
    {
      key: "createdAt",
      header: "Posted",
      render: (c) => <span className="mono">{formatDate(c.createdAt) || "—"}</span>,
      sortValue: (c) => c.createdAt,
    },
  ];

  return (
    <>
      <AdminTable
        rows={rows}
        getId={(c) => c.id}
        columns={columns}
        searchText={(c) => `${c.authorName} ${c.body} ${c.contentId}`}
        searchPlaceholder="Filter comments…"
        typeFilter={{
          label: "On type",
          value: (c) => c.contentType,
          options: CONTENT_TYPES.map((t) => ({ value: t, label: t })),
        }}
        loading={loading}
        error={error ?? undefined}
        emptyLabel="No comments yet."
        onEdit={(c) => setEditing(c)}
        onDelete={async (c) => {
          await adminApi.deleteComment(c.id);
          refetch();
        }}
        bulkActions={[
          {
            label: "Remove selected",
            danger: true,
            confirm: "Soft-delete all selected comments?",
            run: async (sel) => {
              for (const c of sel) await adminApi.deleteComment(c.id);
              refetch();
            },
          },
        ]}
      />
      {editing && (
        <AdminForm
          title={`Edit comment #${editing.id}`}
          fields={FIELDS}
          initial={toForm(editing)}
          onCancel={() => setEditing(null)}
          onSubmit={async (v) => {
            await adminApi.updateComment(editing.id, toRequest(v));
            setEditing(null);
            refetch();
          }}
        />
      )}
    </>
  );
}
